/**
 * Forces Shopify Forms (shadow-root embeds) to render
 * on Horizon theme which uses the View Transitions API (MPA).
 */
'use strict';

const FORM_SELECTOR = 'shopify-forms-embed';
const MAX_RETRIES = 12;
const RETRY_INTERVAL = 200;

// --- Core render call ---

const callShopifyRender = () => {
  if ('ShopifyForms' in window && typeof window.ShopifyForms.render === 'function') {
    window.ShopifyForms.render();
    return true;
  }
  return false;
};

// --- Shadow DOM-aware "unrendered" check ---

const isFormUnrendered = (el) =>
  el.shadowRoot
    ? el.shadowRoot.childElementCount === 0
    : !el.innerHTML.trim();

const hasUnrenderedForms = () => {
  const forms = [...document.querySelectorAll(FORM_SELECTOR)];
  return forms.length > 0 && forms.some(isFormUnrendered);
};

// --- Retry loop (handles late ShopifyForms init) ---

const renderWithRetry = (() => {
  let attempts = 0;
  let timer = null;

  const attempt = () => {
    if (attempts >= MAX_RETRIES) return;
    attempts++;
    if (callShopifyRender()) {
      attempts = 0;
      return;
    }
    timer = setTimeout(attempt, RETRY_INTERVAL);
  };

  return () => {
    clearTimeout(timer);
    attempts = 0;
    attempt();
  };
})();

// --- MutationObserver (debounced) ---

const createObserver = () => {
  let debounce = null;
  const observer = new MutationObserver(() => {
    clearTimeout(debounce);
    debounce = setTimeout(() => {
      if (hasUnrenderedForms()) renderWithRetry();
    }, 150);
  });
  observer.observe(document.body, { childList: true, subtree: true });
};

// --- Bootstrap ---

const init = () => {
  renderWithRetry();

  // ★ Horizon: fires on the page FROM WHICH the user comes.
  // Reset the Forms state to navigation so it doesn't consider itself "failed"
  // on the new page.
  window.addEventListener('pageswap', () => {
    if ('ShopifyForms' in window && typeof window.ShopifyForms.reset === 'function') {
      window.ShopifyForms.reset();
    }
  });

  // ★ Horizon: View Transitions API
  window.addEventListener('pagereveal', () => {
    callShopifyRender();
    renderWithRetry();
  });

  // BFCache (back/forward buttons)
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      callShopifyRender();
      renderWithRetry();
    }
  });

  // Shopify section events (Theme Editor та programmatic section loads)
  document.addEventListener('shopify:section:load', renderWithRetry);
  document.addEventListener('shopify:section:reorder', renderWithRetry);

  // Horizon ThemeEvents
  document.addEventListener('theme:reinit', renderWithRetry);

  // MutationObserver як останній fallback
  createObserver();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}